import { RTC_PROVIDERS } from '../../../providerIds'
import {
  AgoraRtcCompatibilitySurface,
  createAgoraRtcAdapterCapabilities,
  createAgoraRtcCompatibilityProps,
  mergeAgoraRtcCompatibilityProps,
  preloadAgoraRtcUIKitModule,
} from './agoraRtcCompat'

function createEventBus() {
  const listeners = new Map()

  return {
    emit(eventName, payload) {
      const handlers = listeners.get(eventName)

      if (!handlers) {
        return
      }

      handlers.forEach((handler) => handler(payload))
    },
    on(eventName, handler) {
      const handlers = listeners.get(eventName) || new Set()
      handlers.add(handler)
      listeners.set(eventName, handlers)

      return () => {
        const nextHandlers = listeners.get(eventName)

        if (!nextHandlers) {
          return
        }

        nextHandlers.delete(handler)

        if (nextHandlers.size === 0) {
          listeners.delete(eventName)
        }
      }
    },
    clear() {
      listeners.clear()
    },
  }
}

export const AGORA_RTC_SESSION_EVENTS = Object.freeze({
  configChanged: 'config-changed',
  preloadRequested: 'preload-requested',
  joinRequested: 'join-requested',
  leaveRequested: 'leave-requested',
  audioToggleRequested: 'audio-toggle-requested',
  videoToggleRequested: 'video-toggle-requested',
  cameraSwitchRequested: 'camera-switch-requested',
  microphoneSwitchRequested: 'microphone-switch-requested',
  playbackDeviceSwitchRequested: 'playback-device-switch-requested',
  screenshareStartRequested: 'screenshare-start-requested',
  screenshareStopRequested: 'screenshare-stop-requested',
  destroyed: 'destroyed',
})

export function createAgoraRtcSessionHandle({ initialConfig = {} } = {}) {
  const events = createEventBus()
  let compatibilityProps = createAgoraRtcCompatibilityProps(initialConfig)
  let destroyed = false

  function assertActive(methodName) {
    if (!destroyed) {
      return
    }

    throw new Error(`[live/rtc/agora] ${methodName}() called after destroy()`)
  }

  function updateCompatibilityProps(nextConfig) {
    compatibilityProps = mergeAgoraRtcCompatibilityProps(compatibilityProps, nextConfig)
    events.emit(AGORA_RTC_SESSION_EVENTS.configChanged, compatibilityProps)
    return compatibilityProps
  }

  return {
    providerId: RTC_PROVIDERS.agora,
    getConfig() {
      return compatibilityProps
    },
    updateConfig(nextConfig = {}) {
      assertActive('updateConfig')
      return updateCompatibilityProps(nextConfig)
    },
    async preload() {
      assertActive('preload')
      events.emit(AGORA_RTC_SESSION_EVENTS.preloadRequested, compatibilityProps)
      await preloadAgoraRtcUIKitModule()
    },
    async join() {
      assertActive('join')
      await preloadAgoraRtcUIKitModule()
      events.emit(AGORA_RTC_SESSION_EVENTS.joinRequested, compatibilityProps)
      return compatibilityProps
    },
    async leave() {
      assertActive('leave')
      events.emit(AGORA_RTC_SESSION_EVENTS.leaveRequested, compatibilityProps)
      return true
    },
    async setAudioEnabled(enabled) {
      assertActive('setAudioEnabled')
      updateCompatibilityProps({
        rtcProps: {
          enableAudio: Boolean(enabled),
        },
      })
      events.emit(AGORA_RTC_SESSION_EVENTS.audioToggleRequested, Boolean(enabled))
      return compatibilityProps.rtcProps.enableAudio
    },
    async setVideoEnabled(enabled) {
      assertActive('setVideoEnabled')
      updateCompatibilityProps({
        rtcProps: {
          enableVideo: Boolean(enabled),
        },
      })
      events.emit(AGORA_RTC_SESSION_EVENTS.videoToggleRequested, Boolean(enabled))
      return compatibilityProps.rtcProps.enableVideo
    },
    async switchCamera(deviceId) {
      assertActive('switchCamera')
      updateCompatibilityProps({
        rtcProps: {
          cameraDeviceId: deviceId,
        },
      })
      events.emit(AGORA_RTC_SESSION_EVENTS.cameraSwitchRequested, deviceId)
      return deviceId
    },
    async switchMicrophone(deviceId) {
      assertActive('switchMicrophone')
      updateCompatibilityProps({
        rtcProps: {
          microphoneDeviceId: deviceId,
        },
      })
      events.emit(AGORA_RTC_SESSION_EVENTS.microphoneSwitchRequested, deviceId)
      return deviceId
    },
    async setPlaybackDevice(deviceId) {
      assertActive('setPlaybackDevice')
      updateCompatibilityProps({
        rtcProps: {
          playbackDeviceId: deviceId,
        },
      })
      events.emit(AGORA_RTC_SESSION_EVENTS.playbackDeviceSwitchRequested, deviceId)
      return deviceId
    },
    async startScreenshare() {
      assertActive('startScreenshare')
      events.emit(AGORA_RTC_SESSION_EVENTS.screenshareStartRequested, compatibilityProps)
      return compatibilityProps.rtcProps.enableScreensharing === true
    },
    async stopScreenshare() {
      assertActive('stopScreenshare')
      events.emit(AGORA_RTC_SESSION_EVENTS.screenshareStopRequested, compatibilityProps)
      return true
    },
    on(eventName, handler) {
      assertActive('on')
      return events.on(eventName, handler)
    },
    async destroy() {
      if (destroyed) {
        return
      }

      destroyed = true
      events.emit(AGORA_RTC_SESSION_EVENTS.destroyed, compatibilityProps)
      events.clear()
    },
  }
}

export function createAgoraRtcAdapter({ capabilityOverrides = {} } = {}) {
  return {
    id: RTC_PROVIDERS.agora,
    domain: 'rtc',
    compatibilityBridge: 'agora-uikit',
    supports: createAgoraRtcAdapterCapabilities(capabilityOverrides),
    preload: preloadAgoraRtcUIKitModule,
    CompatibilitySurface: AgoraRtcCompatibilitySurface,
    createCompatibilityProps: createAgoraRtcCompatibilityProps,
    createSession(initialConfig = {}) {
      return createAgoraRtcSessionHandle({
        initialConfig,
      })
    },
  }
}

export const agoraRtcAdapter = createAgoraRtcAdapter()
